/**
 * YogaJeans Storefront API Client
 *
 * On the live theme and fixed staging, requests go through the Shopify App
 * Proxy so Shopify forwards them to Cloud Run with the signed proxy query
 * params (signature, shop, timestamp, logged_in_customer_id). On localhost
 * and ephemeral per-PR staging (window.YJ.usesShopifyProxy === false), we
 * call the Cloud Run URL directly and append logged_in_customer_id ourselves
 * since there's no proxy to add it.
 *
 * Base URL is driven by window.YJ.apiBaseUrl, resolved in theme.liquid from
 * the is_staging / storefront_api_url_staging theme settings.
 *
 * Caching: wishlist.get() is stale-while-revalidate — a cache hit returns
 * immediately and triggers a deduped background refresh, which only
 * dispatches wishlist:updated if the server's copy actually differs (e.g.
 * the same account was updated from another browser/device). Writes always
 * hit the API and update the cache with the response. With no customer and
 * no guest wishlist yet, wishlist.get()/remove()/clear() resolve locally to
 * an empty wishlist without any network call.
 *
 * Tracking: events are queued and sent as a single batched POST (debounced,
 * size-capped, and flushed via sendBeacon on page hide) rather than one
 * request per event, since the API's rate limit would reject a click spree
 * sent individually.
 */

/**
 * Thrown when the API returns 401 (no authenticated customer).
 * Catch this specifically to redirect to login rather than show a generic error.
 */
class YJAuthError extends Error {
  constructor(message = 'No authenticated customer') {
    super(message);
    this.name = 'YJAuthError';
  }
}

const YJApiClient = (() => {
  // ── Helpers ───────────────────────────────────────────────────────────────

  /** @returns {string} */
  function getBaseUrl() {
    return window.YJ?.apiBaseUrl ?? '/apps/yj-store';
  }

  /**
   * Builds the request URL, adding logged_in_customer_id ourselves when
   * there's no Shopify App Proxy in front of the API to add it.
   * @param {string} path
   * @returns {string}
   */
  function buildRequestUrl(path) {
    const raw = getBaseUrl() + path;
    if (window.YJ?.usesShopifyProxy !== false || !window.YJ?.customerId) {
      return raw;
    }

    const url = new URL(raw, window.location.origin);
    url.searchParams.set('logged_in_customer_id', window.YJ.customerId);

    // Keep relative bases relative; only absolute (e.g. local dev, ephemeral PR) bases stay absolute.
    const isAbsolute = raw.startsWith('http://') || raw.startsWith('https://');
    return isAbsolute ? url.toString() : url.pathname + url.search;
  }

  // ── Cache ─────────────────────────────────────────────────────────────────

  /**
   * Builds a raw localStorage key for a given namespace + id, independent of
   * whichever id (customer or guest) is currently "active". Used both by
   * cacheKey() below and by merge cleanup, which needs to target a specific
   * guest id's entries even after a customerId has taken over cacheKey()'s
   * normal resolution.
   * @param {string} namespace
   * @param {string} id
   * @returns {string}
   */
  function rawCacheKey(namespace, id) {
    return `yj_${namespace}_${id}`;
  }

  /**
   * Returns a localStorage key scoped to the current customer.
   * Returns null when no customer is logged in (no caching for guests).
   * @param {string} namespace
   * @returns {string|null}
   */
  function cacheKey(namespace) {
    const customerId = window.YJ?.customerId;
    if (customerId) return rawCacheKey(namespace, customerId);
    const guestId = getGuestWishlistId();
    if (guestId) return rawCacheKey(namespace, guestId);
    return null;
  }

  /**
   * Read a cached entry. Returns null on miss or parse error.
   * @param {string} namespace
   * @returns {unknown|null}
   */
  function readCache(namespace) {
    const key = cacheKey(namespace);
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Write a value into the cache. Silently swallows quota errors.
   * @param {string} namespace
   * @param {unknown} data
   */
  function writeCache(namespace, data) {
    const key = cacheKey(namespace);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // Storage quota exceeded — skip caching
    }
  }

  /**
   * Evict a cache entry.
   * @param {string} namespace
   */
  function evictCache(namespace) {
    const key = cacheKey(namespace);
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }

  // ── Guest wishlist identity ───────────────────────────────────────────────
  // Guests have no customerId, so the signed proxy params alone can't route
  // requests to "their" wishlist. Instead, the first request that creates or
  // fetches a guest wishlist gets back its wishlist ID, which we persist
  // here and pass on every subsequent guest add/remove/clear so they all hit
  // the same document. Logged-in customers never need this — it stays null
  // for them, and every helper below treats "no id stored" as a no-op.

  const GUEST_WISHLIST_ID_KEY = 'yj_guest_wishlist_id';

  /**
   * @returns {string|null} the guest wishlist's doc ID, or null if the
   *   customer is logged in (customerId is used instead) or no guest
   *   wishlist has been created/fetched yet.
   */
  function getGuestWishlistId() {
    try {
      return window.localStorage.getItem(GUEST_WISHLIST_ID_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Persists the guest wishlist's doc ID. Only ever called once per guest —
   * subsequent calls are guarded at the call site so we don't clobber it.
   * @param {string} id
   */
  function setGuestWishlistId(id) {
    try {
      window.localStorage.setItem(GUEST_WISHLIST_ID_KEY, id);
    } catch {
      // Storage unavailable/full — the guest just won't get a persisted
      // wishlist across page loads; not worth surfacing an error for.
    }
  }

  /**
   * Clears the persisted guest wishlist doc ID. Only called after a
   * successful merge into a customer's account — the guest doc no longer
   * exists at that point, so nothing should keep referencing it.
   */
  function clearGuestWishlistId() {
    try {
      window.localStorage.removeItem(GUEST_WISHLIST_ID_KEY);
    } catch {
      // ignore
    }
  }

  /**
   * Whether there's anyone (customer or guest) a wishlist could belong to.
   * When false, no wishlist document can exist yet, so reads/mutations can
   * resolve locally instead of asking the API.
   * @returns {boolean}
   */
  function hasWishlistIdentity() {
    return Boolean(window.YJ?.customerId || getGuestWishlistId());
  }

  /**
   * The unsaved, empty wishlist shape returned when there's no identity to
   * look one up by. Mirrors the server's own empty-stub response so callers
   * can't tell the difference.
   * @returns {{ id: string, publicId: null, customerId: null, isGuest: true, handles: string[], variantMap: Record<string,string>, variantDataMap: Record<string,unknown>, createdAt: string, updatedAt: string }}
   */
  function buildEmptyWishlist() {
    const now = new Date().toISOString();
    return {
      id: '',
      publicId: null,
      customerId: null,
      isGuest: true,
      handles: [],
      variantMap: {},
      variantDataMap: {},
      createdAt: now,
      updatedAt: now,
    };
  }

  // ── Dispatch ──────────────────────────────────────────────────────────────

  /**
   * Fires the wishlist:updated custom event so downstream components
   * (WishlistDrawer, WishlistButton) can react immediately to cache changes.
   * @param {string[]} handles
   * @param {Record<string,string>} [variantMap]
   * @param {'optimistic'|'server'|'revalidate'} [source] - why the event fired
   */
  function dispatchWishlistUpdate(handles, variantMap = {}, source = 'optimistic') {
    if (typeof document === 'undefined') return;
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles, variantMap, source } }));
  }

  // ── Core fetch ────────────────────────────────────────────────────────────

  /**
   * Core fetch wrapper. Goes through the Shopify App Proxy when one is in
   * front of the API; otherwise buildRequestUrl() adds logged_in_customer_id.
   *
   * @param {string} path  - e.g. '/wishlist' or '/wishlist/some-handle'
   * @param {RequestInit} [options]
   * @returns {Promise<unknown>}
   */
  async function request(path, options = {}) {
    const url = buildRequestUrl(path);

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers ?? {}),
      },
    });

    if (!response.ok) {
      let message = `API error ${response.status}`;
      try {
        const body = await response.json();
        message = body?.message ?? JSON.stringify(body) ?? message;
      } catch { /* ignore */ }
      throw new YJAuthError(message);
    }

    return response.json();
  }

  // ── Wishlist ──────────────────────────────────────────────────────────────

  /** Shared in-flight promise so concurrent get()/revalidate calls share one request. */
  let _wishlistFetchInflight = null;

  /**
   * Fetches the wishlist from the API and writes the result to cache.
   * Concurrent calls (including background revalidation) share one request.
   *
   * NOTE: A GET never creates a wishlist document server-side, so the
   * response carries no real doc id for guests (it's an empty stub). The
   * guest wishlist id is only established on the first add — see
   * addToWishlist — so we deliberately don't persist anything from here.
   * @returns {Promise<unknown>}
   */
  function fetchWishlist() {
    if (!_wishlistFetchInflight) {
      _wishlistFetchInflight = request(withGuestWishlistId('/wishlist'))
        .then((data) => {
          writeCache('wishlist', data);
          return data;
        })
        .finally(() => { _wishlistFetchInflight = null; });
    }
    return _wishlistFetchInflight;
  }

  /**
   * Whether two wishlist states differ in a way worth re-rendering for:
   * handle membership (order-independent) or a changed variant selection.
   * @param {unknown} a
   * @param {unknown} b
   * @returns {boolean}
   */
  function wishlistsDiffer(a, b) {
    const handlesA = a?.handles ?? [];
    const handlesB = b?.handles ?? [];
    if (handlesA.length !== handlesB.length) return true;
    const setA = new Set(handlesA);
    if (handlesB.some((h) => !setA.has(h))) return true;

    const variantMapA = a?.variantMap ?? {};
    const variantMapB = b?.variantMap ?? {};
    const keysA = Object.keys(variantMapA);
    const keysB = Object.keys(variantMapB);
    if (keysA.length !== keysB.length) return true;
    return keysA.some((handle) => variantMapA[handle] !== variantMapB[handle]);
  }

  /**
   * Refreshes the wishlist cache in the background so a copy created or
   * changed in another browser/device eventually converges. Never rejects
   * and never rolls back the cache — a failed or stale-guest-id revalidation
   * should leave whatever is already cached untouched.
   * @param {unknown} previous - the cached value this revalidation started from
   */
  function revalidateWishlist(previous) {
    fetchWishlist()
      .then((data) => {
        if (wishlistsDiffer(previous, data)) {
          dispatchWishlistUpdate(data.handles, data.variantMap, 'revalidate');
        }
      })
      .catch(() => { /* best-effort — stale cache stands until the next revalidation */ });
  }

  /**
   * Fetch the customer's (or guest's) wishlist.
   * With no identity yet (no customer, no guest wishlist created), resolves
   * locally to an empty wishlist — no network call. On a cache hit, returns
   * the cached copy immediately and revalidates from the API in the
   * background (stale-while-revalidate). On a cache miss, awaits the API.
   * @returns {Promise<{ customerId: string, handles: string[] }>}
   */
  async function getWishlist() {
    if (!hasWishlistIdentity()) return buildEmptyWishlist();

    const cached = readCache('wishlist');
    if (cached) {
      revalidateWishlist(cached);
      return cached;
    }

    return fetchWishlist();
  }

  /**
   * Derives the optimistic wishlist state after adding a handle.
   * Pure function — takes the previous cached state and returns the next one.
   * @param {string} handle
   * @param {string|number} [variantId]
   * @returns {(previous: unknown) => { handles: string[], variantMap: Record<string,string> }}
   */
  function withHandleAdded(handle, variantId) {
    return (previous) => ({
      ...(previous ?? {}),
      handles: [...new Set([...(previous?.handles ?? []), handle])],
      variantMap: {
        ...(previous?.variantMap ?? {}),
        ...(variantId ? { [handle]: String(variantId) } : {}),
      },
    });
  }

  /**
   * Derives the optimistic wishlist state after removing a handle.
   * Pure function — takes the previous cached state and returns the next one.
   * @param {string} handle
   * @returns {(previous: unknown) => { handles: string[], variantMap: Record<string,string> }}
   */
  function withHandleRemoved(handle) {
    return (previous) => {
      const variantMap = { ...(previous?.variantMap ?? {}) };
      delete variantMap[handle];
      return {
        ...(previous ?? {}),
        handles: (previous?.handles ?? []).filter((h) => h !== handle),
        variantMap,
      };
    };
  }

  /**
   * Reverts the wishlist cache to its pre-optimistic-update state and
   * notifies listeners. Evicts the cache entirely if nothing was cached
   * before the optimistic update ran.
   * @param {unknown|null} previous
   */
  function rollbackWishlistCache(previous) {
    if (previous === null) {
      evictCache('wishlist');
    } else {
      writeCache('wishlist', previous);
    }
    dispatchWishlistUpdate(previous?.handles ?? [], previous?.variantMap ?? {}, 'server');
  }

  /**
   * Appends the guest wishlist ID as a query param when one exists. A no-op
   * for logged-in customers (routed by customerId instead) and for guests
   * who haven't created a wishlist yet. The API always expects this ID as
   * a query param — never in the request body — so every guest wishlist
   * request (add/remove/clear) is routed through this helper.
   * @param {string} path
   * @returns {string}
   */
  function withGuestWishlistId(path) {
    const id = getGuestWishlistId();
    if (!id) return path;

    const [pathname, search] = path.split('?');
    const params = new URLSearchParams(search);
    params.set('id', id);
    return `${pathname}?${params}`;
  }

  /**
   * Shared read-modify-write flow for wishlist mutations: applies an
   * optimistic cache update immediately (so the UI feels instant), performs
   * the real API call, and either commits the server's response as the new
   * cache or rolls back on failure. A successful mutation also evicts the
   * cached share link, since add/remove change the wishlist those points to.
   *
   * @param {(previous: unknown) => unknown} computeOptimisticState - derives the optimistic state from what's currently cached
   * @param {() => Promise<unknown>} performApiCall - executes the actual mutation request
   * @returns {Promise<unknown>} the server's response
   */
  async function mutateWishlistOptimistically(computeOptimisticState, performApiCall) {
    const previous = readCache('wishlist');
    const optimistic = computeOptimisticState(previous);
    writeCache('wishlist', optimistic);
    dispatchWishlistUpdate(optimistic.handles, optimistic.variantMap, 'optimistic');

    try {
      const data = await performApiCall();
      writeCache('wishlist', data);
      evictCache('shareLink');
      return data;
    } catch (err) {
      rollbackWishlistCache(previous);
      throw err;
    }
  }

  /**
   * Add a product handle to the wishlist.
   * Optimistically updates the cache and dispatches wishlist:updated immediately,
   * then syncs to the API. Reverts the cache and re-dispatches if the API fails.
   * @param {string} handle
   * @param {string|number} [variantId] - optional Shopify variant ID
   * @returns {Promise<{ customerId: string, handles: string[], variantMap: Record<string,string> }>}
   */
  async function addToWishlist(handle, variantId) {
    return mutateWishlistOptimistically(
      withHandleAdded(handle, variantId),
      async () => {
        const body = { handle };
        if (variantId) body.variantId = String(variantId);
        const data = await request(withGuestWishlistId('/wishlist'), { method: 'POST', body: JSON.stringify(body) });

        // The first guest add is what actually creates the wishlist document
        // server-side. Persist (or refresh) its doc id so every subsequent
        // add/remove/clear routes to the same document. The id can also change
        // when a stale, previously-purged guest id gets a brand new doc, so we
        // always sync to whatever the server just returned.
        if (data?.isGuest && data?.id && data.id !== getGuestWishlistId()) {
          setGuestWishlistId(data.id);
        }
        return data;
      },
    );
  }

  /**
   * Remove a product handle from the wishlist.
   * With no identity yet, there's nothing to remove from — resolves locally
   * without a network call. Otherwise optimistically updates the cache and
   * dispatches wishlist:updated immediately, then syncs to the API. Reverts
   * the cache and re-dispatches if the API fails.
   * @param {string} handle
   * @returns {Promise<{ customerId: string, handles: string[] }>}
   */
  async function removeFromWishlist(handle) {
    if (!hasWishlistIdentity()) return buildEmptyWishlist();
    return mutateWishlistOptimistically(
      withHandleRemoved(handle),
      () => request(withGuestWishlistId(`/wishlist/${encodeURIComponent(handle)}`), { method: 'DELETE' }),
    );
  }

  /**
   * Clear the entire wishlist and evict the cache — including the cached
   * share link, since it points at a wishlist whose contents just changed.
   * With no identity yet, there's nothing on the server to clear — resolves
   * locally without a network call. Dispatches wishlist:updated with an
   * empty handle list either way, so the drawer (and any other listeners)
   * clear their display immediately.
   * @returns {Promise<{ message: string }>}
   */
  async function clearWishlist() {
    if (!hasWishlistIdentity()) {
      dispatchWishlistUpdate([], {}, 'optimistic');
      return { message: 'Wishlist cleared' };
    }
    const data = await request(withGuestWishlistId('/wishlist'), { method: 'DELETE' });
    evictCache('wishlist');
    evictCache('shareLink');
    dispatchWishlistUpdate([], {}, 'server');
    return data;
  }

  /**
   * Returns (or lazily generates) a stable public share URL for this wishlist.
   * Guests route via ?id=<guestId>; logged-in customers are routed by the
   * Shopify proxy's logged_in_customer_id param automatically.
   *
   * Serves from localStorage cache on a hit; falls back to the API on a
   * miss. The cache is invalidated by addToWishlist, removeFromWishlist,
   * and clearWishlist, since any of those change what the link points to.
   * Dispatches wishlist:updated (with the currently cached handles, since
   * this endpoint doesn't return them) so the drawer stays in sync.
   * @returns {Promise<{ url: string }>}
   */
  async function fetchShareLink() {
    const wishlist = await getWishlist();
    // Locale-aware path rendered by Liquid in theme.liquid, so a link shared from /fr opens the French page.
    const base = `${window.location.origin}${window.YJ?.wishlistUrl || '/pages/wishlist'}`;
    if (wishlist?.handles?.length === 0) {
      return `${base}?id=empty`;
    }
    return `${base}?id=${encodeURIComponent(wishlist.publicId)}`;
  }

  /**
   * Resolves a public share token (from a share URL) to the full wishlist response.
   * No auth required — share links are intentionally public.
   * @param {string} publicId - the short nanoid token from the share URL
   * @returns {Promise<{ id: string, handles: string[], variantMap: Record<string,string> }>}
   */
  async function resolveShareLink(publicId) {
    return request(`/wishlist/resolve-share-link?publicId=${encodeURIComponent(publicId)}`);
  }

  // ── Guest → customer merge (on login) ────────────────────────────────────

  /** Shared in-flight promise so concurrent callers don't double-merge. */
  let _mergeInflight = null;

  /**
   * Merges a pre-login guest wishlist into the now-logged-in customer's
   * wishlist, unioning handles (deduped by product, not variant) server-side.
   * No-ops immediately (no network call) when the customer isn't logged in
   * or no guest wishlist id is stored — the common case for most logins.
   *
   * On success, evicts the stale guest-scoped cache entries, clears the
   * guest id, writes the merged result into the customer's cache, and
   * dispatches wishlist:updated so any buttons/drawer already on the page
   * pick it up immediately.
   *
   * On failure, the guest id is deliberately left in place so the next page
   * load retries automatically — no extra retry logic needed.
   * @returns {Promise<unknown|null>}
   */
  function mergeGuestWishlistIfNeeded() {
    const customerId = window.YJ?.customerId;
    const guestId = getGuestWishlistId();
    if (!customerId || !guestId) return Promise.resolve(null);

    if (_mergeInflight) return _mergeInflight;

    _mergeInflight = (async () => {
      try {
        const merged = await request(`/wishlist/merge?guestId=${encodeURIComponent(guestId)}`, { method: 'POST' });

        // The guest doc is gone server-side post-merge — drop its cache
        // entries explicitly, since cacheKey() can no longer reach them now
        // that a customerId is set (it resolves to the customer's key instead).
        try { localStorage.removeItem(rawCacheKey('wishlist', guestId)); } catch { /* ignore */ }
        try { localStorage.removeItem(rawCacheKey('shareLink', guestId)); } catch { /* ignore */ }
        clearGuestWishlistId();

        writeCache('wishlist', merged);
        evictCache('shareLink');
        dispatchWishlistUpdate(merged?.handles ?? [], merged?.variantMap ?? {}, 'server');
        return merged;
      } catch (err) {
        console.error('Failed to merge guest wishlist into account:', err);
        return null;
      } finally {
        _mergeInflight = null;
      }
    })();

    return _mergeInflight;
  }

  // ── Tracking identity ─────────────────────────────────────────────────────
  // No anonymous visitor identifier exists elsewhere in the theme, so tracking
  // gets its own pair: visitorId persists across visits (localStorage), sessionId
  // rolls over after a period of inactivity (sessionStorage + a last-seen stamp,
  // since sessionStorage alone never expires within a tab).

  const VISITOR_ID_KEY = 'yj_visitor_id';
  const SESSION_ID_KEY = 'yj_session_id';
  const SESSION_LAST_SEEN_KEY = 'yj_session_last_seen';
  const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

  /** In-memory fallback for browsers/modes where storage throws (e.g. private browsing). */
  let _visitorIdFallback = null;
  let _sessionIdFallback = null;

  /** @returns {string} */
  function generateId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /** @returns {string} */
  function getVisitorId() {
    try {
      let id = window.localStorage.getItem(VISITOR_ID_KEY);
      if (!id) {
        id = generateId();
        window.localStorage.setItem(VISITOR_ID_KEY, id);
      }
      return id;
    } catch {
      return _visitorIdFallback ?? (_visitorIdFallback = generateId());
    }
  }

  /** @returns {string} */
  function getSessionId() {
    try {
      const now = Date.now();
      const lastSeen = Number(window.sessionStorage.getItem(SESSION_LAST_SEEN_KEY) ?? 0);
      let id = window.sessionStorage.getItem(SESSION_ID_KEY);
      if (!id || now - lastSeen > SESSION_IDLE_TIMEOUT_MS) {
        id = generateId();
        window.sessionStorage.setItem(SESSION_ID_KEY, id);
      }
      window.sessionStorage.setItem(SESSION_LAST_SEEN_KEY, String(now));
      return id;
    } catch {
      return _sessionIdFallback ?? (_sessionIdFallback = generateId());
    }
  }

  // ── Tracking queue ────────────────────────────────────────────────────────
  // Batched so a filter-clicking spree can't outrun the API's per-minute rate
  // limit; flushed on an idle debounce, a size threshold, or page unload.

  const TRACKING_PATH = '/tracking/events';
  const TRACKING_FLUSH_DEBOUNCE_MS = 2000;
  const TRACKING_FLUSH_SIZE_THRESHOLD = 20;

  let _trackingQueue = [];
  let _trackingFlushTimer = null;

  function scheduleTrackingFlush() {
    if (_trackingFlushTimer) return;
    _trackingFlushTimer = setTimeout(() => {
      _trackingFlushTimer = null;
      flushTrackingQueue();
    }, TRACKING_FLUSH_DEBOUNCE_MS);
  }

  /**
   * Queues a tracking event. Never throws — a tracking failure must never
   * break the caller's own click/filter handling.
   * @param {string} eventName
   * @param {Record<string, unknown>} payload
   * @param {Record<string, unknown>} [context]
   */
  function track(eventName, payload, context) {
    try {
      _trackingQueue.push({
        eventName,
        occurredAt: new Date().toISOString(),
        context: context ?? {},
        payload: payload ?? {},
      });
      if (_trackingQueue.length >= TRACKING_FLUSH_SIZE_THRESHOLD) {
        flushTrackingQueue();
      } else {
        scheduleTrackingFlush();
      }
    } catch {
      // best-effort — never break the caller
    }
  }

  /**
   * Sends whatever is queued. `useBeacon` is set on page-unload paths, where
   * a normal fetch could be cancelled mid-flight by navigation.
   * @param {boolean} [useBeacon]
   */
  function flushTrackingQueue(useBeacon = false) {
    if (_trackingQueue.length === 0) return;
    const events = _trackingQueue;
    _trackingQueue = [];
    if (_trackingFlushTimer) {
      clearTimeout(_trackingFlushTimer);
      _trackingFlushTimer = null;
    }

    try {
      const body = JSON.stringify({ visitorId: getVisitorId(), sessionId: getSessionId(), events });
      const url = buildRequestUrl(TRACKING_PATH);

      if (useBeacon && navigator.sendBeacon) {
        const sent = navigator.sendBeacon(
          url,
          new Blob([body], { type: 'text/plain;charset=UTF-8' }) // was 'application/json'
        );
        if (sent) return;
      }

      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => { /* best-effort */ });
    } catch {
      // tracking must never surface an error to the caller
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushTrackingQueue(true);
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => flushTrackingQueue(true));
  }

  // ── Public API ────────────────────────────────────────────────────────────

  return {
    getBaseUrl,
    AuthError: YJAuthError,
    cache: {
      read:  (ns) => readCache(ns),
      write: (ns, data) => writeCache(ns, data),
      evict: (ns) => evictCache(ns),
    },
    wishlist: {
      get:           getWishlist,
      add:           addToWishlist,
      remove:        removeFromWishlist,
      clear:         clearWishlist,
      fetchShareLink:   fetchShareLink,
      resolveShareLink: resolveShareLink,
      mergeGuestIfNeeded: mergeGuestWishlistIfNeeded,
    },
    tracking: {
      track:         track,
      flush:         () => flushTrackingQueue(false),
      getVisitorId:  getVisitorId,
      getSessionId:  getSessionId,
    },
  };
})();

window.YJApiClient = YJApiClient;

// primarily for unit testing purposes
if (typeof module !== 'undefined') {
  module.exports = YJApiClient;
}